import { useTranslation } from "react-i18next"

import { surfaceColor, surfaceText } from "../../../constants/surface-tokens"

/**
 * All / Unread / Needs attention.
 *
 * Every count is the server's own, counted the same way the list behind the
 * chip is — a chip labelled with a number from a different rule is worse than
 * a chip with no number.
 */

export type NotificationFilter = "all" | "unread" | "needs_attention"

export const NOTIFICATION_FILTERS: NotificationFilter[] = [
  "all",
  "unread",
  "needs_attention"
]

export interface NotificationCounts {
  all?: number
  unread?: number
  needs_attention?: number
}

interface Props {
  value: NotificationFilter
  counts?: NotificationCounts | null
  onChange: (next: NotificationFilter) => void
  size?: "small" | "default"
}

export function NotificationFilterChips({
  value,
  counts,
  onChange,
  size = "default"
}: Props) {
  const { t } = useTranslation()
  const compact = size === "small"

  return (
    <div
      role="tablist"
      aria-label={t("notifications.filters.label", "Filter notifications")}
      style={{ display: "flex", gap: 6, flexWrap: "wrap" }}
    >
      {NOTIFICATION_FILTERS.map((filter) => {
        const active = filter === value
        const count = counts?.[filter]
        return (
          <button
            key={filter}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`notification-filter-${filter}`}
            onClick={() => onChange(filter)}
            style={{
              ...surfaceText.button,
              fontSize: compact ? 12 : 13,
              padding: compact ? "4px 10px" : "5px 12px",
              borderRadius: 999,
              cursor: "pointer",
              border: `1px solid ${active ? surfaceColor.teal : surfaceColor.border}`,
              background: active ? surfaceColor.tealPale : surfaceColor.surface,
              color: active ? surfaceColor.teal : surfaceColor.inkMuted,
              whiteSpace: "nowrap"
            }}
          >
            {t(`notifications.filters.${filter}`, defaultFilterLabel(filter))}
            {typeof count === "number" && ` (${count})`}
          </button>
        )
      })}
    </div>
  )
}

function defaultFilterLabel(filter: NotificationFilter) {
  if (filter === "all") return "All"
  if (filter === "unread") return "Unread"
  return "Needs attention"
}
